import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Activity, RefreshCw } from "lucide-react";

// Grade geográfica aproximada do Brasil (col, row) por UF
const UF_GRID = {
  RR: [2, 0], AP: [4, 0],
  AM: [1, 1], PA: [3, 1], MA: [5, 1], CE: [7, 1], RN: [8, 1],
  AC: [0, 2], RO: [1, 2], MT: [3, 2], TO: [4, 2], PI: [6, 2], PB: [8, 2],
  DF: [4, 3], GO: [3, 3], BA: [6, 3], PE: [7, 3], AL: [8, 3],
  MS: [2, 4], MG: [5, 4], SE: [7, 4], ES: [6, 5],
  SP: [4, 5], RJ: [5, 5],
  PR: [3, 6], SC: [4, 7], RS: [3, 8],
};

const CORES = {
  ativos: "#16A34A", alerta: "#F59E0B", inativos: "#DC2626",
  sem_demanda: "#64748B", baixa: "#126BFF", agenda_fechada: "#94A3B8",
};
const LABELS = {
  ativo_participante: ["Ativo", "bg-[#DCFCE7] text-[#166534]"],
  alerta_inatividade: ["Em alerta", "bg-aj-amber-soft text-aj-orange-hover"],
  inativo_por_ausencia: ["Inativo por ausência", "bg-red-100 text-[#B91C1C]"],
  sem_demanda_regiao: ["Sem demanda na região", "bg-gray-100 text-gray-600"],
  ativo_com_baixa_utilizacao: ["Baixa utilização", "bg-[#DBEAFE] text-[#142766]"],
  agenda_fechada: ["Agenda fechada", "bg-gray-100 text-gray-500"],
};

function corPredominante(b) {
  if (!b) return "#E2E8F0";
  if (b.inativos > 0) return CORES.inativos;
  if (b.alerta > 0) return CORES.alerta;
  if (b.sem_demanda > 0 && b.ativos === 0) return CORES.sem_demanda;
  if (b.ativos > 0) return CORES.ativos;
  return "#CBD5E1";
}

export default function AtividadeRede() {
  const [dados, setDados] = useState(null);
  const [ufSel, setUfSel] = useState(null);
  const [rodando, setRodando] = useState(false);

  const carregar = () => api.get("/admin/atividade-rede/painel")
    .then(r => setDados(r.data)).catch(e => toast.error(formatApiError(e)));
  useEffect(() => { carregar(); }, []);

  const executar = async () => {
    setRodando(true);
    try {
      const r = await api.post("/admin/atividade-rede/executar");
      toast.success(`Verificação executada: ${r.data.alertados} alertado(s) · ${r.data.inativados} inativado(s)`);
      carregar();
    } catch (e) { toast.error(formatApiError(e)); }
    setRodando(false);
  };

  if (!dados) return <div className="aj-card p-10 text-center text-aj-text-muted">Carregando painel…</div>;
  const t = dados.totais;
  const montadores = ufSel ? dados.montadores.filter(m => m.uf === ufSel) : dados.montadores;

  return (
    <div className="space-y-5" data-testid="atividade-rede-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">Rede</div>
          <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-aj-blue-deep mt-1 flex items-center gap-2">
            <Activity size={24} className="text-aj-orange" /> Atividade da Rede
          </h1>
        </div>
        <Button onClick={executar} disabled={rodando} variant="outline" data-testid="rede-executar-btn">
          <RefreshCw size={15} className={`mr-1.5 ${rodando ? "animate-spin" : ""}`} /> Executar verificação 60+30
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="rede-totais">
        {[["Total", t.total, "#142766"], ["Ativos", t.ativos, CORES.ativos],
          ["Em alerta", t.alerta, CORES.alerta], ["Inativos", t.inativos, CORES.inativos],
          ["Sem demanda", t.sem_demanda, CORES.sem_demanda], ["Baixa utilização", t.baixa, CORES.baixa]].map(([l, v, c]) => (
          <div key={l} className="aj-card px-3 py-3">
            <div className="text-[10px] uppercase font-bold text-aj-text-muted">{l}</div>
            <div className="font-extrabold text-2xl" style={{ color: c }}>{v}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="aj-card p-5" data-testid="rede-mapa">
          <h2 className="font-bold text-aj-blue-deep mb-1">Mapa da rede por UF</h2>
          <p className="text-[11px] text-aj-text-muted mb-3">Clique em um estado para filtrar a lista. Cor = situação predominante.</p>
          <div className="relative" style={{ width: "100%", maxWidth: 420, aspectRatio: "9/9.4", margin: "0 auto" }}>
            {Object.entries(UF_GRID).map(([uf, [col, row]]) => {
              const b = dados.por_uf[uf];
              return (
                <button key={uf} onClick={() => setUfSel(ufSel === uf ? null : uf)}
                  className={`absolute rounded-md flex flex-col items-center justify-center font-bold transition-transform hover:scale-110 ${ufSel === uf ? "ring-2 ring-aj-blue-deep" : ""}`}
                  style={{
                    left: `${col * 11}%`, top: `${row * 10.5}%`, width: "10%", height: "9.5%",
                    background: corPredominante(b), color: b ? "#fff" : "#64748B",
                    opacity: b ? 1 : 0.35, fontSize: 11,
                  }}
                  data-testid={`rede-uf-${uf}`} title={b ? `${uf}: ${b.total} montador(es)` : uf}>
                  {uf}{b ? <span style={{ fontSize: 9 }}>{b.total}</span> : null}
                </button>
              );
            })}
          </div>
          <div className="flex gap-3 flex-wrap mt-3 text-[10px] font-semibold text-aj-text-muted">
            {[["Ativos", CORES.ativos], ["Alerta", CORES.alerta], ["Inativos", CORES.inativos], ["Sem demanda", CORES.sem_demanda]].map(([l, c]) => (
              <span key={l} className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded" style={{ background: c }} /> {l}</span>
            ))}
          </div>
        </div>

        <div className="aj-card p-5" data-testid="rede-regioes">
          <h2 className="font-bold text-aj-blue-deep mb-3">Regiões sem demanda</h2>
          {dados.regioes_sem_demanda.length === 0 && (
            <div className="text-sm text-aj-text-muted">Nenhuma região com montadores sem demanda no momento. 👍</div>
          )}
          <div className="space-y-2">
            {dados.regioes_sem_demanda.map(r => (
              <div key={r.uf} className="border border-border rounded-lg px-3 py-2 text-sm">
                <span className="font-bold">{r.uf}</span> · {r.montadores} montador(es) sem demanda ·
                <span className="text-aj-text-muted"> {r.cidades.slice(0, 5).join(", ")}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="aj-card p-5" data-testid="rede-lista">
        <h2 className="font-bold text-aj-blue-deep mb-3">
          Montadores {ufSel ? `— ${ufSel}` : ""} ({montadores.length})
          {ufSel && <button onClick={() => setUfSel(null)} className="ml-2 text-xs text-aj-orange hover:underline">limpar filtro</button>}
        </h2>
        <div className="space-y-1.5">
          {montadores.map(m => {
            const [label, cls] = LABELS[m.atividade] || ["—", "bg-gray-100"];
            return (
              <Link key={m.id} to={`/admin/montadores/${m.id}`}
                className="flex items-center justify-between border border-border rounded-lg px-3 py-2 text-sm hover:border-aj-orange transition-colors"
                data-testid={`rede-montador-${m.id}`}>
                <span className="font-semibold flex-1 truncate">{m.nome}</span>
                <span className="text-xs text-aj-text-muted mx-3">{m.cidade}/{m.uf} · score {Number(m.score ?? 0).toFixed(0)}</span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${cls}`}>{label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
